import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import DatePickerButton from '../ui/date-picker-button';
import { TrendingUp, RefreshCw, BarChart3, Calendar, Target } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
const PerformanceBenchmarkChart = ({
  title = "Performance Benchmark: Designed",
  startDate: propStartDate,
  endDate: propEndDate,
  selectedKPI,
  filter
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(propStartDate || new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(propEndDate || new Date().toISOString().split('T')[0]);
  const [selectedKpi, setSelectedKpi] = useState(selectedKPI || 'performance_ratio');
  const [benchmarkType, setBenchmarkType] = useState(filter || 'Designed');
  
  // Update when props change
  useEffect(() => {
    if (propStartDate) setStartDate(propStartDate);
    if (propEndDate) setEndDate(propEndDate);
    if (selectedKPI) setSelectedKpi(selectedKPI);
    if (filter) setBenchmarkType(filter);
  }, [propStartDate, propEndDate, selectedKPI, filter]);
  const {
    toast
  } = useToast();
  const kpiOptions = [{
    value: 'performance_ratio',
    label: 'Performance Ratio (%)'
  }, {
    value: 'plant_availability',
    label: 'Plant Availability (%)'
  }, {
    value: 'grid_availability',
    label: 'Grid Availability (%)'
  }, {
    value: 'specific_yield',
    label: 'Specific Yield (kWh/kWp)'
  }, {
    value: 'specific_power',
    label: 'Specific Power (kW/kWp)'
  }, {
    value: 'temp_corr_ratio',
    label: 'Temp Corr PR (%)'
  }, {
    value: 'energy_export',
    label: 'Energy Export (kWh)'
  }, {
    value: 'energy_import',
    label: 'Energy Import (kWh)'
  }, {
    value: 'irradiation',
    label: 'Tilt POA Irradiation (kWh/m²)'
  }, {
    value: 'expected_energy',
    label: 'Expected Energy (kWh)'
  }, {
    value: 'dc_cuf_daily',
    label: 'DC CUF (%)'
  }, {
    value: 'ac_cuf_daily',
    label: 'AC CUF (%)'
  }];
  useEffect(() => {
    fetchBenchmarkData();
  }, [startDate, endDate, selectedKpi, benchmarkType]);
  const fetchBenchmarkData = async () => {
    try {
      setIsLoading(true);
      const startDateTime = startDate + ' 00:00:00';
      const endDateTime = endDate + ' 23:59:59';
      console.log('Performance Benchmark: Fetching data with params:', {
        startDateTime,
        endDateTime,
        plant_id: API_CONFIG.PLANT_ID,
        selectedKpi
      });

      // Fetch observed data
      const observedResponse = await apiService.getKpiReadingsByTags({
        start_date: startDateTime,
        end_date: endDateTime,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID],
        tags_list: [selectedKpi]
      });
      console.log('Performance Benchmark: Observed Response:', observedResponse);

      // Fetch PVSyst data for budgeted values
      const pvSystResponse = await apiService.getPvSystData({
        plant_id: API_CONFIG.PLANT_ID
      });
      console.log('Performance Benchmark: PVSyst Response:', pvSystResponse);
      if (observedResponse.data && pvSystResponse.data) {
        // Group observed data by day and calculate daily averages
        const dailyObservedData = {};
        const observedArray = Array.isArray(observedResponse.data) ? observedResponse.data : observedResponse.data?.body || [];
        observedArray.forEach(item => {
          if (item.readings && item.readings[0] && item.readings[0].KPI) {
            const kpiReading = item.readings[0].KPI.find(kpi => kpi[selectedKpi]);
            if (kpiReading) {
              const actualValue = kpiReading[selectedKpi];
              const date = new Date(item.timestamp).toISOString().split('T')[0]; // Get date only

              if (!dailyObservedData[date]) {
                dailyObservedData[date] = [];
              }
              dailyObservedData[date].push(actualValue);
            }
          }
        });
        const processedData = [];

        // Get budgeted value from PVSyst data
        let designedValue = 85; // Default value
        if (pvSystResponse.data.json_object && pvSystResponse.data.json_object.PVSyst) {
          const pvSystItem = pvSystResponse.data.json_object.PVSyst.find(pvsyst => pvsyst.code === `PLANT_${selectedKpi.toUpperCase()}_PVSYST`);
          if (pvSystItem) {
            const currentMonth = new Date().getMonth() + 1;
            designedValue = pvSystItem.valueMatrix[currentMonth] || 85;
          }
        }

        // Process daily averages
        Object.keys(dailyObservedData).forEach(date => {
          const dailyValues = dailyObservedData[date];
          const averageActualValue = dailyValues.reduce((sum, val) => sum + val, 0) / dailyValues.length;
          const formattedDate = new Date(date).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          });
          const delta = averageActualValue - designedValue;
          processedData.push({
            date: formattedDate,
            designed: Number(designedValue.toFixed(2)),
            actual: Number(averageActualValue.toFixed(2)),
            delta: Number(delta.toFixed(2))
          });
        });
        console.log('Performance Benchmark: Processed Data:', processedData);

        // Sort by date
        processedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setData(processedData);
      }
    } catch (error) {
      console.error('Error fetching benchmark data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch benchmark data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const formatNumber = num => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2
    }).format(num);
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (active && payload && payload.length) {
      return <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </p>
          {payload.map((entry, index) => <p key={index} className="text-sm" style={{
          color: entry.color
        }}>
              {entry.name}: {formatNumber(entry.value)}%
            </p>)}
        </div>;
    }
    return null;
  };
  const CustomLegend = ({
    payload
  }) => {
    return <div className="flex justify-center space-x-6 mt-4">
        {payload?.map((entry, index) => <div key={index} className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: entry.color
        }}></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {entry.value}
            </span>
          </div>)}
      </div>;
  };
  return <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-blue-500" />
            <span>{title}</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={fetchBenchmarkData} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Controls */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              KPI to Show
            </label>
            <select value={selectedKpi} onChange={e => setSelectedKpi(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              {kpiOptions.map(option => <option key={option.value} value={option.value}>
                  {option.label}
                </option>)}
            </select>
          </div>
          <div>
            <DatePickerButton selectedDate={startDate} onDateChange={setStartDate} label="Start Date" placeholder="Select Start Date" />
          </div>
          <div>
            <DatePickerButton selectedDate={endDate} onDateChange={setEndDate} label="End Date" placeholder="Select End Date" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Performance Benchmark
            </label>
            <div className="flex space-x-2">
              <Button variant={benchmarkType === 'Designed' ? 'default' : 'outline'} size="sm" onClick={() => setBenchmarkType('Designed')}>
                Designed
              </Button>
              <Button variant={benchmarkType === 'Contractual' ? 'default' : 'outline'} size="sm" onClick={() => setBenchmarkType('Contractual')}>
                Contractual
              </Button>
            </div>
          </div>
        </div>

        {/* Chart */}
        {isLoading ? <div className="space-y-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-64 w-full" />
          </div> : data.length > 0 ? <div className="space-y-4">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data} margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 80
          }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{
              fontSize: 10
            }} angle={-45} textAnchor="end" height={80} />
                <YAxis tick={{
              fontSize: 12
            }} label={{
              value: selectedKpi === 'performance_ratio' ? 'Performance Ratio (%)' : selectedKpi === 'plant_availability' ? 'Plant Availability (%)' : selectedKpi === 'grid_availability' ? 'Grid Availability (%)' : selectedKpi === 'specific_yield' ? 'Specific Yield (kWh/kWp)' : selectedKpi === 'specific_power' ? 'Specific Power (kW/kWp)' : selectedKpi === 'temp_corr_ratio' ? 'Temp Corr PR (%)' : selectedKpi === 'energy_export' ? 'Energy Export (kWh)' : selectedKpi === 'energy_import' ? 'Energy Import (kWh)' : selectedKpi === 'irradiation' ? 'Tilt POA Irradiation (kWh/m²)' : selectedKpi === 'expected_energy' ? 'Expected Energy (kWh)' : selectedKpi === 'dc_cuf_daily' ? 'DC CUF (%)' : selectedKpi === 'ac_cuf_daily' ? 'AC CUF (%)' : 'Value',
              angle: -90,
              position: 'insideLeft'
            }} />
                <YAxis yAxisId="right" orientation="right" tick={{
              fontSize: 12
            }} label={{
              value: 'Delta (%)',
              angle: 90,
              position: 'insideRight'
            }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend content={<CustomLegend />} />
                <Bar dataKey="designed" fill="#FF69B4" name={`${benchmarkType} (%)`} radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" fill="#176BEF" name="Observed (%)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="delta" fill="#F59E0B" name="Delta (%)" radius={[4, 4, 0, 0]} yAxisId="right">
                  {data.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.delta >= 0 ? '#10B981' : '#DC2626'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Target className="h-5 w-5 text-orange-500" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg {benchmarkType}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {formatNumber(data.reduce((sum, d) => sum + d.designed, 0) / data.length)}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg Actual
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {formatNumber(data.reduce((sum, d) => sum + d.actual, 0) / data.length)}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Avg Delta
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {formatNumber(data.reduce((sum, d) => sum + d.delta, 0) / data.length)}%
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Data Points
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                    {data.length}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div> : <div className="text-center py-12">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No benchmark data available</p>
            <p className="text-sm text-gray-400 mt-2">Try selecting a different date range or KPI</p>
          </div>}
      </CardContent>
    </Card>;
};
export default PerformanceBenchmarkChart;